import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWatchInvoiceNotifications } from "../../../../../../notifications/hooks/useWatchInvoiceNotifications";
import { useInvoicePaymentStatus } from "../../ClientView/hooks/useInvoicePaymentStatus";
import { syncInvoicePayment } from "../../ClientView/actions/syncInvoicePayment";
import { invalidateAfterInvoiceChange } from "../../../utils/invoiceQueryInvalidation";
import type { IInvoice } from "../../../../create/interfaces/invoice.interfaces";

type UseInvoiceDetailsViewSyncParams = {
  invoiceUid: string;
  invoice: IInvoice | undefined;
  refetchInvoice?: () => void;
  /** When true, runs a one-time PayPal sync on load (same guard as DetailsView). */
  hasPayPalPending: boolean;
};

/**
 * Admin invoice preview: keeps React Query + legacy quote listeners aligned with socket payment
 * events, push notifications, and manual "Verify payment status" / auto-sync flows.
 */
export function useInvoiceDetailsViewSync({
  invoiceUid,
  invoice,
  refetchInvoice,
  hasPayPalPending,
}: UseInvoiceDetailsViewSyncParams) {
  const queryClient = useQueryClient();
  const syncAttemptedRef = useRef<string | null>(null);

  const billingJobEventUids = useMemo(
    () => (invoice?.billingJobEvents ?? []).map((e) => e.uid),
    [invoice?.billingJobEvents],
  );

  const quoteId = useMemo(
    () => (invoice as IInvoice & { quoteId?: number })?.quoteId ?? (invoice as any)?.quote?.id ?? undefined,
    [invoice],
  );

  const invalidateRelatedQueries = useCallback(() => {
    invalidateAfterInvoiceChange(queryClient, {
      invoiceUid,
      jobEventUids: billingJobEventUids,
      quoteId,
    });
  }, [queryClient, invoiceUid, billingJobEventUids, quoteId]);

  useWatchInvoiceNotifications({
    onNotificationArrived: (notification) => {
      const meta = notification?.meta;
      if (!meta) return;
      if (meta.uid === invoiceUid) {
        invalidateRelatedQueries();
        return;
      }
      if (invoice?.identifierId && meta.identifierId === invoice.identifierId) {
        invalidateRelatedQueries();
      }
    },
  });

  useInvoicePaymentStatus({
    invoiceUid,
    onEvent: () => {
      invalidateRelatedQueries();
      refetchInvoice?.();
    },
    onUpdated: () => {
      invalidateRelatedQueries();
      refetchInvoice?.();
    },
  });

  useEffect(() => {
    if (!hasPayPalPending || !invoice?.uid || syncAttemptedRef.current === invoice.uid) return;
    syncAttemptedRef.current = invoice.uid;
    let cancelled = false;
    (async () => {
      try {
        const result = await syncInvoicePayment(invoice.uid);
        if (!cancelled && result?.synced) {
          invalidateRelatedQueries();
          refetchInvoice?.();
        }
      } catch {
        // Silent: user can use "Verify payment status" to retry
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice?.uid, hasPayPalPending, refetchInvoice, invalidateRelatedQueries]);

  return {
    invalidateRelatedQueries,
    quoteId,
    billingJobEventUids,
  };
}
